import { LMS_LOGOUT_INFO, LMS_USER } from "../../component/constant";
import { dateTimeReviver } from "../helper/json-helper";
import { ApiError } from "./api-error";

export function handleResponse<TResponse>(response: Response): Promise<TResponse> {
    if (!response.ok) {
        return handleFailedResponse(response) as Promise<TResponse>;
    } else {
        return response.text().then((json) => {
            if (json !== '') {
                return JSON.parse(json, dateTimeReviver);
            }
            return undefined;
        }) as Promise<TResponse>
    }
}

export function handleResponseNoContent(response: Response) {
    if (!response.ok) {
        return handleFailedResponse(response);
    }
}

async function handleFailedResponse(response: Response) {
    if (response.status === 401) {
        localStorage.removeItem(LMS_USER);
        sessionStorage.setItem(LMS_LOGOUT_INFO, "You have been logged out, please login again");
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));

        const errorType = await response.json().then(e => e.ErrorType).catch(() => undefined);
        throw new ApiError({ errorCode: 401, errorDescription: "Unauthorized", errorType });
    }

    try {
        var error = await response.json();
        const errors = parseErrors(error.Errors);
        const errorDescription = errors.length > 0 ? errors[0] : getDefaultDescription(response.status);
        if (response.status === 400) {
            throw new ApiError({ errorCode: 400, errorDescription, errorType: error.ErrorType, errors });
        } else if (response.status === 403) {
            throw new ApiError({ errorCode: 403, errorDescription, errorType: error.ErrorType, errors });
        } else if (response.status === 404) {
            throw new ApiError({ errorCode: 404, errorDescription, errorType: error.ErrorType, errors });
        } else if (response.status === 409) {
            throw new ApiError({ errorCode: 409, errorDescription, errorType: error.ErrorType, errors });
        } else {
            throw new ApiError({ errorCode: response.status, errorDescription, errorType: error.ErrorType, errors });
        }
    } catch (jsonError) {
        if (jsonError instanceof ApiError) throw jsonError;
        throw new ApiError({ errorCode: response.status, errorDescription: response.statusText, errorType: undefined });
    }
}

function parseErrors(errors: unknown): string[] {
    if (!Array.isArray(errors)) return [];
    return errors
        .filter((e): e is string => typeof e === 'string' && e.trim() !== '')
        .filter(e => !e.trimStart().startsWith('at '))
        .map(e => e.trim());
}

function getDefaultDescription(status: number): string {
    switch (status) {
        case 400: return "Bad request";
        case 403: return "Forbidden";
        case 404: return "Not found";
        case 409: return "Conflict";
        default: return "An error occurred";
    }
}